/**
 * Files (the Drive-like area, docs/features/files.md): folders, files with versions, pages and databases placed in
 * folders, shortcuts, sharing like Google Drive (people, user groups, channels, the workspace, link access; Viewer /
 * Commenter / Editor), stars, recents, trash, storage, and a change feed with cursors for sync clients.
 *
 * Internally "drive" (routes `/v1/w/:ws/drive/…`); people see "Files" / "Arquivos".
 *
 * Bytes reuse the file upload pipeline (files.ts): `createUpload` with `purpose: "drive"` (and `sha256`, so identical
 * content is stored once and the upload is instant), then `createDriveItem` / `addDriveVersion` with the `fileId`.
 * Every version is a file; `DriveItem.file` is the current one.
 *
 * Access: an item's owner, plus everyone its own or any ancestor folder's `DriveShare`s name (roles add up, the highest
 * wins), plus link access. Guests (restricted members) only get what names them, their groups or their channels.
 */
import { z } from "zod";
import { FileRef, Id, Timestamp } from "./entities.ts";

/**
 * Largest file in Files: 5 TB, Google Drive's per-file limit (R2 objects go up to 4.995 TiB, about 5.49 TB). Imports
 * from Google Drive take files up to this. Chat attachments keep files.ts's 1 GiB.
 */
export const DRIVE_MAX_FILE_BYTES = 5_000_000_000_000;
/**
 * Largest direct upload into Files: the same 5 TB. Parts up to files.ts SINGLE_UPLOAD_MAX_BYTES go through the Worker
 * (100 MB request bodies); past about 1 TB (10,000 such parts, R2's limit) they are bigger, so the client PUTs them
 * straight to R2 on presigned URLs (UploadPlan mode "direct").
 */
export const DRIVE_MAX_UPLOAD_BYTES = DRIVE_MAX_FILE_BYTES;
/** R2 multipart: at most this many parts, each up to 5 GiB (all but the last at least 5 MiB, the same size). */
export const R2_MAX_PARTS = 10_000;
const MIB = 1_048_576;

/**
 * Part size of an R2 multipart upload of `size` bytes: `min` (the usual part size) while that keeps it within
 * R2_MAX_PARTS, else the smallest whole MiB that does (5 TB → 477 MiB parts).
 */
export function multipartPartBytes(size: number, min: number): number {
  const needed = Math.ceil(Math.ceil(size / R2_MAX_PARTS) / MIB) * MIB;
  return Math.max(min, needed);
}
/** Trash keeps items this long, then deletes them forever (Google Drive: 30 days). */
export const DRIVE_TRASH_TTL_MS = 30 * 86_400_000;
/** Older versions are kept up to this many per file (plus "keep forever" ones). */
export const DRIVE_MAX_VERSIONS = 100;

/** "file": bytes with versions; "page" / "database": a Bundle page or database placed in Files; "shortcut": a pointer. */
export const DriveItemKind = z.enum(["folder", "file", "page", "database", "shortcut"]);
export type DriveItemKind = z.infer<typeof DriveItemKind>;

/** Roles, lowest first. "owner" is never granted through a share (transfer ownership instead). */
export const DriveRole = z.enum(["viewer", "commenter", "editor", "owner"]);
export type DriveRole = z.infer<typeof DriveRole>;

/** Who a share names. "workspace": everyone in the workspace except guests. */
export const DrivePrincipalKind = z.enum(["user", "group", "channel", "workspace"]);
export type DrivePrincipalKind = z.infer<typeof DrivePrincipalKind>;

/**
 * General access ("link sharing"): restricted, anyone in the workspace who has the link, or anyone with the link
 * ("anyone": a public URL https://<app>/f/<token>; workspace members who open it get the link role like "workspace",
 * everyone else, guests and signed-out visitors included, can only view and download). Admins can turn "anyone" off
 * with the workspace's `publicFiles` policy (SharingSettings: "members" = any editor of the item may pick it, "admins" =
 * owners/admins only, "off" = nobody, and existing anyone links stop resolving until it's back on).
 */
export const DriveLinkScope = z.enum(["restricted", "workspace", "anyone"]);
export type DriveLinkScope = z.infer<typeof DriveLinkScope>;

/** Where an attachment was shared (chat, mail, a page or a database row): Drive's "shared in". */
export const DriveSource = z.object({
  kind: z.enum(["channel", "dm", "mail", "page", "row"]),
  /** Channel / DM id, page id, mail thread id or database id. */
  id: Id,
  /** Channel name, page title, mail subject… (absent for DMs: clients derive the name from the people). */
  title: z.string().optional(),
  /** The message it was posted in (chat). */
  messageId: Id.optional(),
});
export type DriveSource = z.infer<typeof DriveSource>;

export const DriveItem = z.object({
  id: Id,
  kind: DriveItemKind,
  name: z.string().min(1).max(255),
  /** The folder it lives in; absent = the owner's My files root. */
  parentId: Id.optional(),
  ownerId: Id,
  /** kind "file": the current version's bytes (`FileRef.url` streams it; the version list has the older ones). */
  file: FileRef.optional(),
  /** kind "page" / "database": the page or database id. */
  pageId: Id.optional(),
  /** kind "shortcut": the item it points to. */
  targetId: Id.optional(),
  /** kind "shortcut": what the target is, for the icon. */
  targetKind: DriveItemKind.optional(),
  /** Bytes of the current version (files), 0 otherwise. */
  size: z.number().int().nonnegative(),
  mime: z.string().optional(),
  createdAt: Timestamp,
  /** Content or name changed. */
  modifiedAt: Timestamp,
  modifiedBy: Id,
  /** Bumped on every change to the item; send it back as `baseVersion` to detect conflicting writes. */
  version: z.number().int().positive(),
  /** Set while in the trash (this item, or a folder above it, was moved there). */
  trashedAt: Timestamp.optional(),
  /** The caller's effective role. */
  role: DriveRole,
  starred: z.boolean(),
  /** Shared with anyone besides the owner (shares or link). */
  shared: z.boolean(),
  /** When the caller last opened it. */
  viewedAt: Timestamp.optional(),
});
export type DriveItem = z.infer<typeof DriveItem>;

/**
 * An attachment from chat, mail, a page or a database row, listed in Files (Recent, Shared with me, search, and my
 * "From conversations"). Opens the file page; it isn't a DriveItem until someone adds a shortcut or makes a copy.
 */
export const DriveAttachment = z.object({
  file: FileRef,
  uploaderId: Id,
  sharedAt: Timestamp,
  source: DriveSource,
});
export type DriveAttachment = z.infer<typeof DriveAttachment>;

/** One row of a Files list: an item or an attachment. */
export const DriveEntry = z.object({ item: DriveItem.optional(), attachment: DriveAttachment.optional() });
export type DriveEntry = z.infer<typeof DriveEntry>;

export const DriveView = z.enum(["my", "shared", "recent", "starred", "trash", "conversations"]);
export type DriveView = z.infer<typeof DriveView>;

export const DriveSort = z.enum(["name", "modified", "viewed", "size", "shared"]);
export type DriveSort = z.infer<typeof DriveSort>;

/** Search and the type filter chip. */
export const DriveTypeFilter = z.enum(["folder", "document", "spreadsheet", "presentation", "pdf", "image", "video", "audio", "archive", "page", "other"]);
export type DriveTypeFilter = z.infer<typeof DriveTypeFilter>;

/**
 * GET /v1/w/:ws/drive/items?view=&folderId=&q=&type=&owner=&sort=&dir=&cursor=
 * `folderId` lists that folder (any view); otherwise the view's root. `q` searches names across everything I can see.
 */
export const ListDriveResponse = z.object({
  entries: z.array(DriveEntry),
  /** The folder listed and its ancestors I can see, root first (breadcrumb). */
  path: z.array(DriveItem),
  /** Pass back for the next page; absent = done. */
  cursor: z.string().optional(),
});
export type ListDriveResponse = z.infer<typeof ListDriveResponse>;

export const DriveItemResponse = z.object({ item: DriveItem });
export type DriveItemResponse = z.infer<typeof DriveItemResponse>;

/**
 * POST /v1/w/:ws/drive/items — a folder (`name`), a file (`fileId` of a finished `purpose: "drive"` upload), a page or
 * database placed in a folder (`pageId`), or a shortcut (`targetId`). Same name in the same folder is allowed (Drive).
 */
export const CreateDriveItemRequest = z.object({
  kind: DriveItemKind,
  name: z.string().min(1).max(255),
  parentId: Id.optional(),
  fileId: Id.optional(),
  pageId: Id.optional(),
  targetId: Id.optional(),
  /** Retry-safe: the same clientId from the same person returns the same item. */
  clientId: z.string().max(64).optional(),
});
export type CreateDriveItemRequest = z.infer<typeof CreateDriveItemRequest>;

/** PATCH /v1/w/:ws/drive/items/:itemId — rename, move (`parentId`, null = My files root), star (per person). */
export const UpdateDriveItemRequest = z.object({
  name: z.string().min(1).max(255).optional(),
  parentId: Id.nullable().optional(),
  starred: z.boolean().optional(),
  /** Rename / move only if nobody changed the item since (else 409 `conflict`). */
  baseVersion: z.number().int().positive().optional(),
});
export type UpdateDriveItemRequest = z.infer<typeof UpdateDriveItemRequest>;

/** POST …/items/:itemId/copy — files, pages and databases (not folders, like Google Drive). → DriveItemResponse */
export const CopyDriveItemRequest = z.object({ name: z.string().min(1).max(255).optional(), parentId: Id.optional() });
export type CopyDriveItemRequest = z.infer<typeof CopyDriveItemRequest>;

/** POST /v1/w/:ws/drive/trash — move to trash (owner, or editors of the folder it is in). → 204 */
export const TrashDriveItemsRequest = z.object({ itemIds: z.array(Id).min(1).max(100) });
export type TrashDriveItemsRequest = z.infer<typeof TrashDriveItemsRequest>;

/** POST /v1/w/:ws/drive/restore and /delete-forever take the same body. Empty trash: DELETE /v1/w/:ws/drive/trash. */

export const DriveVersion = z.object({
  id: Id,
  file: FileRef,
  size: z.number().int().nonnegative(),
  authorId: Id,
  /** The device that uploaded it (sync clients: "MacBook Pro"). */
  device: z.string().optional(),
  createdAt: Timestamp,
  keepForever: z.boolean(),
  current: z.boolean(),
});
export type DriveVersion = z.infer<typeof DriveVersion>;

/** GET …/items/:itemId/versions → newest first. */
export const DriveVersionsResponse = z.object({ versions: z.array(DriveVersion) });
export type DriveVersionsResponse = z.infer<typeof DriveVersionsResponse>;

/**
 * POST …/items/:itemId/versions — a new version from a finished upload (editors). With `baseVersion`, a write that
 * raced another one fails with 409 `conflict` (sync clients then keep a "conflicted copy").
 */
export const AddDriveVersionRequest = z.object({
  fileId: Id,
  baseVersion: z.number().int().positive().optional(),
  device: z.string().max(100).optional(),
});
export type AddDriveVersionRequest = z.infer<typeof AddDriveVersionRequest>;

/** PATCH …/versions/:versionId — keep forever; POST …/versions/:versionId/restore makes it the current one again. */
export const UpdateDriveVersionRequest = z.object({ keepForever: z.boolean() });
export type UpdateDriveVersionRequest = z.infer<typeof UpdateDriveVersionRequest>;

export const DriveShare = z.object({
  principal: DrivePrincipalKind,
  /** The user, group or channel ("workspace": absent). */
  principalId: Id.optional(),
  role: DriveRole,
  /** Set when it comes from a folder above (not editable here). */
  inheritedFrom: Id.optional(),
});
export type DriveShare = z.infer<typeof DriveShare>;

/** Why the workspace's `publicFiles` policy keeps someone from choosing "Anyone with the link". */
export const DriveAnyoneBlocked = z.enum(["off", "admins"]);
export type DriveAnyoneBlocked = z.infer<typeof DriveAnyoneBlocked>;

/** An item's general access in the share dialog. */
export const DriveLink = z.object({
  scope: DriveLinkScope,
  role: DriveRole,
  /**
   * scope "anyone": the public URL (https://<app>/f/<token>). The token is made when the scope first becomes "anyone"
   * and kept when it flips back and forth. Absent while the workspace's `publicFiles` policy is "off".
   */
  url: z.string().optional(),
});
export type DriveLink = z.infer<typeof DriveLink>;

/** GET …/items/:itemId/sharing — the share dialog. */
export const DriveSharingResponse = z.object({
  ownerId: Id,
  shares: z.array(DriveShare),
  link: DriveLink,
  /** The caller may change shares (owner and editors). */
  canManage: z.boolean(),
  /** The caller may set general access to "anyone" (canManage, and the workspace's `publicFiles` policy allows them). */
  canLinkAnyone: z.boolean(),
  /** Why "anyone" isn't offered by policy: "off" (turned off for the workspace) or "admins" (owners/admins only). Absent = allowed. */
  anyoneBlocked: DriveAnyoneBlocked.optional(),
});
export type DriveSharingResponse = z.infer<typeof DriveSharingResponse>;

/**
 * PUT …/items/:itemId/sharing — set this item's own shares and link access (inherited ones stay). `link.scope:
 * "anyone"` follows the workspace's `publicFiles` policy: 403 `public_links_off` when it's "off", 403 `forbidden`
 * when it's "admins" and the caller isn't an owner/admin. `role: "owner"` on
 * a user transfers ownership (owner only). `notify` posts a DM from me with the link to newly added people (Drive's
 * "Notify people").
 */
export const UpdateDriveSharingRequest = z.object({
  shares: z.array(z.object({ principal: DrivePrincipalKind, principalId: Id.optional(), role: DriveRole })).max(200),
  link: z.object({ scope: DriveLinkScope, role: DriveRole }).optional(),
  notify: z.boolean().optional(),
  message: z.string().max(2000).optional(),
});
export type UpdateDriveSharingRequest = z.infer<typeof UpdateDriveSharingRequest>;

/** GET /v1/w/:ws/drive/storage — usage of the workspace pool and mine. */
export const DriveStorageResponse = z.object({
  /** Workspace quota in bytes. */
  quota: z.number().int().nonnegative(),
  /** Everything stored in the workspace (Files, attachments, versions, trash). */
  used: z.number().int().nonnegative(),
  mine: z.object({
    files: z.number().int().nonnegative(),
    attachments: z.number().int().nonnegative(),
    versions: z.number().int().nonnegative(),
    trash: z.number().int().nonnegative(),
  }),
});
export type DriveStorageResponse = z.infer<typeof DriveStorageResponse>;

/** One change in the feed: the item as it is now, or `removed` (deleted forever, or I lost access). */
export const DriveChange = z.object({
  itemId: Id,
  item: DriveItem.optional(),
  removed: z.boolean().optional(),
});
export type DriveChange = z.infer<typeof DriveChange>;

/**
 * GET /v1/w/:ws/drive/changes?cursor= — everything that changed since `cursor` for the caller (no cursor: a start
 * cursor and no changes, like Drive's getStartPageToken). Page until `hasMore` is false; keep `cursor`.
 */
export const DriveChangesResponse = z.object({
  changes: z.array(DriveChange),
  cursor: z.string(),
  hasMore: z.boolean(),
});
export type DriveChangesResponse = z.infer<typeof DriveChangesResponse>;

// ---------- Public links ("Anyone with the link", no auth) ----------

/** An item as a public visitor sees it: no ids of people, no shares, no versions. */
export const PublicDriveItem = z.object({
  id: Id,
  kind: DriveItemKind,
  name: z.string(),
  /** Bytes (files), 0 otherwise. */
  size: z.number().int().nonnegative(),
  mime: z.string().optional(),
  modifiedAt: Timestamp,
  /** The owner's display name. */
  ownerName: z.string(),
});
export type PublicDriveItem = z.infer<typeof PublicDriveItem>;

/**
 * GET /v1/public/drive/:token?item=<id> — an "Anyone with the link" item (no auth; rate-limited per IP; `noindex`).
 * `item` opens something inside a shared folder (any non-trashed descendant). Revoked links, public links turned off,
 * and trashed or deleted items answer 404.
 */
export const PublicDriveResponse = z.object({
  workspaceName: z.string(),
  /** Lets a signed-in member open it in the app (`GET /v1/w/:ws/drive/items/:id?via=link`). */
  workspaceId: Id,
  item: PublicDriveItem,
  /** Breadcrumb from the shared item down to `item`, root first, `item` last (just `item` when it is the shared one). */
  path: z.array(PublicDriveItem),
  /** Folders: what's inside, folders first, then by name (pages and databases are listed but can't be opened). */
  children: z.array(PublicDriveItem).optional(),
  /**
   * Files: the browser can render it inline (passive media, PDF, plain text); `content` answers it inline. Otherwise
   * download only (`content?download=1`, or `content` answers it as an attachment anyway).
   */
  previewable: z.boolean(),
});
export type PublicDriveResponse = z.infer<typeof PublicDriveResponse>;

/** Why a visitor reports a public link (Google Drive's "Report abuse" categories). */
export const PublicDriveReportReason = z.enum(["spam", "malware", "phishing", "sexual", "violence", "hate", "harassment", "copyright", "personalInfo", "other"]);
export type PublicDriveReportReason = z.infer<typeof PublicDriveReportReason>;

/**
 * POST /v1/public/drive/:token/report — "Report abuse" on a public link (no auth; rate-limited per IP). Kept with the
 * workspace's Files data, written to the audit log, sent to the workspace's owners and admins (a realtime notification)
 * and emailed to Bundle support. 204; unknown or revoked links answer 404 like the reads.
 */
export const ReportPublicDriveRequest = z.object({
  reason: PublicDriveReportReason,
  /** Optional details (trimmed; at most 1000 characters). */
  note: z.string().max(1000).optional(),
  /** The item inside a shared folder being reported (absent: the shared item). */
  item: Id.optional(),
});
export type ReportPublicDriveRequest = z.infer<typeof ReportPublicDriveRequest>;
